import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import InlineGroup from 'components/Common/InlineGroup'
import FlashOfferSummary from './FlashOfferSummary'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import { getOfferExtra, OFFER_VIEWS_24_HOURS_THRESHOLD } from 'selectors/offerExtraSelector'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchOfferExtraById } from 'actions/OfferActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { fetchCruiseLines } from 'actions/CruiseActions'
import { OptimizelyClientSideExperiments, OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import FlashHotelPropertyDetails from './FlashHotelPropertyDetails'
import FlashHotelPropertyDetailsExperimentVariant from './FlashHotelPropertyDetailsExperimentVariant'
import ContactUSP from '../ContactUSP'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlashHotelUSPs from './FlashHotelUSPs'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import config from 'constants/config'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import LineDollarSignIcon from 'components/Luxkit/Icons/line/LineDollarSignIcon'
import Divider from 'components/Luxkit/Divider'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import FlashHotelViewsUrgencyBanner from './FlashHotelViewsUrgencyBanner'
import PropertyMapV2 from 'components/Common/PropertyMap/PropertyMapV2'
import Group from 'components/utils/Group'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(28)};
    padding-bottom: ${rem(28)};
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  checkIn: moment.Moment;
  offer: App.HotelOffer;
  regionCode: string;
  onWatchVideo: () => void;
  className?: string;
  onViewPackageOptions: () => void;
  flightDestination?: App.Airport;
  offerExtra: App.OfferExtra;
  shouldShowSalesPhoneUSP: boolean;
  defaultDepositAmountPercentage: number;
  cruiseLines: Array<App.CruiseLine>;
  shareModalConfig: ReturnType<typeof getShareModalConfig>
}

function FlashHotelIntro(props: Props) {
  const {
    checkIn,
    offer,
    onWatchVideo,
    className,
    onViewPackageOptions,
    flightDestination,
    offerExtra,
    shouldShowSalesPhoneUSP,
    cruiseLines,
    shareModalConfig,
    regionCode,
  } = props
  const property = offer.property
  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const offerPageTruncateText = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.offerPageTruncateText)
  const offerPageMapInSummary = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.offerPageMapInSummary)
  const isUrgencyTodayActive = offerExtra && offerExtra.viewsInLast24Hours >= OFFER_VIEWS_24_HOURS_THRESHOLD

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  useEffect(() => {
    dispatch(fetchCruiseLines())
  }, [dispatch])

  return (<div>
    {isUrgencyTodayActive && <CSSBreakpoint max="mobile">
      <FlashHotelViewsUrgencyBanner numberOfViews={offerExtra.viewsInLast24Hours} />
    </CSSBreakpoint> }
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <VerticalSpacer gap={20}>
        <TripGuard>
          <CSSBreakpoint max="mobile" >
            <InlineGroup vertical="center" spacing="sm" horizontal="end">
              <ShareModal shareConfig={shareModalConfig}>
                <ShareButton />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </InlineGroup>
          </CSSBreakpoint>
        </TripGuard>
        <InlineGroup wraps horizontal="between" spacing="sm" vertical="center">
          <LabelGroup>
            <OfferUrgencyLabels offer={offer} showLimitedAvailability />
            {!offer.hasHiddenCancellationPolicy && (<PromotionLabel variant="default" icon={<LineCalendarIcon />}>
              7 days free cancellation
            </PromotionLabel>)}
            {!offer.disableBestPriceGuarantee && config.bestPriceGuarantee && (<PromotionLabel variant="default" icon={<LineDollarSignIcon />}>
              Best price guarantee
            </PromotionLabel>)}
            {regionCode !== 'IN' && (
              <PromotionLabel variant="default" icon={<LineUserIcon />}>
                24-7 support
              </PromotionLabel>
            )}
          </LabelGroup>
          <TripGuard inverse>
            <CSSBreakpoint max="mobile" >
              <ShareModal shareConfig={shareModalConfig}>
                <ShareButton outdent="right" />
              </ShareModal>
            </CSSBreakpoint>
          </TripGuard>
        </InlineGroup>
        {offerPageTruncateText && (
          <FlashHotelPropertyDetailsExperimentVariant
            property={property}
            offer={offer}
            cruiseLines={cruiseLines}
            onWatchVideo={onWatchVideo}
            shareModalConfig={shareModalConfig}
          />)}
        {!offerPageTruncateText && (<>
          <FlashHotelPropertyDetails
            property={property}
            offer={offer}
            cruiseLines={cruiseLines}
            onWatchVideo={onWatchVideo}
            shareModalConfig={shareModalConfig}
          />
          {offer.additionalDescription && <>
            <Divider kind="primary"/>
            <MarkdownRender type="compact" content={offer.additionalDescription} />
          </>}
        </>
        )}
        <Divider kind="primary" />
        <FlashHotelUSPs offer={offer} checkIn={checkIn} />
        {!offerPageMapInSummary && <Divider kind="primary" />}
        {offerPageMapInSummary && <CSSBreakpoint max="mobile">
          <Group gap={16} direction="vertical">
            <Divider kind="primary" />
            <PropertyMapV2 offer={offer}/>
            <Divider kind="primary" />
          </Group>
        </CSSBreakpoint>}
        {shouldShowSalesPhoneUSP && <ContactUSP />}
      </VerticalSpacer>
      <SummaryColumn>
        <SummaryBlock>
          <CSSBreakpoint min="tablet">
            <InlineGroup vertical="center" spacing="sm" horizontal="end">
              <TripGuard inverse>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareButton outdent="right" />
                </ShareModal>
              </TripGuard>
              <TripGuard>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareButton />
                </ShareModal>
              </TripGuard>
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
            </InlineGroup>
          </CSSBreakpoint>
          <div>
            {isUrgencyTodayActive && <FlashHotelViewsUrgencyBanner numberOfViews={offerExtra.viewsInLast24Hours} />}
            <HotelSummary
              onViewPackageOptions={onViewPackageOptions}
              flightDestination={flightDestination}
            />
            {offerPageMapInSummary && <PropertyMapV2 offer={offer} />}
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
  </div>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice, 1000),
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  checkIn: state.checkInDate,
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  offerExtra: getOfferExtra(appState, state.offer?.id),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  cruiseLines: appState.cruise.cruiseLines,
}))(FlashHotelIntro)
